import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import { useAuth } from '../App';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Bell, Check } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const NotificationsPage = () => {
  const { sessionToken } = useAuth();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMessages();
  }, []);

  const fetchMessages = async () => {
    try {
      const response = await axios.get(`${API}/messages`, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
      setMessages(response.data);
    } catch (error) {
      console.error('Failed to fetch messages:', error);
      toast.error('Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (messageId) => {
    try {
      await axios.put(
        `${API}/messages/${messageId}/read`,
        {},
        { headers: { Authorization: `Bearer ${sessionToken}` } }
      );
      
      setMessages(prev => 
        prev.map(msg => 
          msg._id === messageId ? { ...msg, read: true } : msg
        )
      );
      
      toast.success('Message marked as read');
    } catch (error) {
      console.error('Failed to mark message as read:', error);
      toast.error('Failed to update message');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-sky-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-slate-600 font-medium">Loading messages...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-slate-900 font-heading mb-2" data-testid="notifications-title">Messages & Notifications</h1>
          <p className="text-slate-500 text-lg">Stay updated with school communications</p>
        </div>

        {messages.length === 0 ? (
          <Card className="p-12 rounded-2xl border-slate-100 bg-white text-center">
            <Bell className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-slate-900 mb-2">No messages yet</h3>
            <p className="text-slate-500">You'll see notifications and messages here</p>
          </Card>
        ) : (
          <div className="space-y-4">
            {messages.map((message) => (
              <Card
                key={message._id}
                className={`p-6 rounded-2xl border-slate-100 hover:shadow-md transition-shadow ${
                  !message.read ? 'bg-sky-50 border-sky-200' : 'bg-white'
                }`}
                data-testid={`message-${message._id}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 bg-sky-100 rounded-full flex items-center justify-center">
                        <Bell className="w-5 h-5 text-sky-500" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-slate-900">School Notification</h3>
                        <p className="text-xs text-slate-500">
                          {message.created_at ? format(new Date(message.created_at), 'PPp') : 'Just now'}
                        </p>
                      </div>
                      {!message.read && (
                        <Badge className="bg-sky-500 text-white">New</Badge>
                      )}
                    </div>
                    <p className="text-slate-700 mt-3">{message.message}</p>
                  </div>
                  
                  {!message.read && (
                    <button
                      onClick={() => markAsRead(message._id)}
                      className="ml-4 p-2 hover:bg-slate-100 rounded-full transition-colors"
                      data-testid={`mark-read-${message._id}`}
                    >
                      <Check className="w-5 h-5 text-slate-600" />
                    </button>
                  )}
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default NotificationsPage;